import dayjs from 'dayjs'

const $str = window.$str;
const lineColor = "#6A727C1A"; // 图表splitLine的颜色
const textColor = "#8b93aa"; // 图表文字的颜色

const defaultMin = dayjs().format('YYYY-MM-DD 00:00:00')
const defaultMax = dayjs().format('YYYY-MM-DD 23:59:59')

/**
 * 获取linesChart的配置对象
 * @param {*} chartData 图表要渲染的数据
 * @param {*} min X轴的最小坐标值
 * @param {*} max X轴的最大坐标值
 * @param {*} demand 需量值【要展示的某一条线的数据值】
 * @param {*} isGroupeLink 是否组联
 * @param {*} selectType 选择类型：station|eLink|eBlock
 * @param {*} meterModel 表类型：0-虚拟表，1-实体表
 * @returns 
 */
export function getChartOption(chartData = {}, min = defaultMin, max = defaultMax, demand, isGroupeLink, selectType, meterModel = 1) {
  const { min: min1, max: max1 } = getY1MinMaxNum(chartData);
  const { min: min0, max: max0 } = getY0MinMaxNum(chartData);
  return {
    color: ['#1D77F5', '#FFC859', '#2AA523'],
    /** 坐标系-X轴 */
    xAxis: [
      {
        gridIndex: 0, // 坐标系idx
        name: $str.getKey('数据分析/时间'), // 轴线名称
        type: 'time', // 时间轴
        nameLocation: 'end',
        min: min, // 最小坐标值
        max: max, // 最大坐标值
        boundaryGap: false,
        axisLine: { // 坐标轴轴线的配置
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          show: false // 隐藏标签
        },
        position: 'bottom', // 轴线展示位置
        nameTextStyle: {
          color: '#999',
        },
      },
      {
        gridIndex: 1, // 坐标系idx
        name: $str.getKey('数据分析/时间'), // 轴线名称
        type: 'time', // 时间轴
        nameLocation: 'end',
        min: min, // 最小坐标值
        max: max, // 最大坐标值
        boundaryGap: false,
        axisLine: {
          show: true,
          lineStyle: {
            color: lineColor, // 使用lineColor
            type: 'dashed' // 虚线
          }
        },
        axisTick: {
          show: true
        },
        position: 'top', // 轴线展示在顶部（即两个grid之间的位置）
        nameTextStyle: {
          color: textColor
        },
        axisLabel: {
          color: textColor, // 灰色坐标文字
          show: true, // 显示时间标签
        },
      },
    ],
    /** 坐标系-Y轴 */
    yAxis: [
      {
        gridIndex: 0, // 坐标系idx
        name: $str.getKey('数据分析/SOC(%)'),
        min: min0 ?? 0,
        max: max0 ?? 100,
        interval: 20,
        // nameGap: 15, // 坐标轴名称与轴线之间的距离。
        nameLocation: 'end',
        nameTextStyle: {
          align: 'center',
          color: textColor,
          padding: [0, 0, 0, 35]
        },
        type: 'value',
        splitLine: {
          lineStyle: {
            color: '#eee',
            type: 'dashed'
          }
        },
        axisLabel: {
          formatter: function (value) {
            return Math.round(value);
          },
          color: textColor,
        },
        axisLine: {
          show: false
        }
      },
      {
        gridIndex: 1, // 坐标系idx
        name: $str.getKey('数据分析/功率(kW)'),
        min: min1 ?? null,
        max: max1 ?? null,
        // nameGap: 15, // 坐标轴名称与轴线之间的距离。
        nameLocation: 'start', // 名称位置在开始位置（Y轴下方）
        nameTextStyle: {
          align: 'center',
          color: '#999'
        },
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: {
            color: '#eee',
            type: 'dashed'
          }
        },
        axisLabel: {
          formatter: function (value) {
            // 确保返回整数格式
            return Math.round(value).toString();
          },
          color: '#999',
        },
        axisLine: {
          show: false
        },
        splitNumber: 10
      }
    ],
    /** 直角坐标系内绘图网格 */
    grid: [
      {
        top: '8%',
        left: '1%',
        right: '1%',
        height: '30%',
        containLabel: true
      },
      {
        top: '40%', // 从第一个grid下方开始，减小间距
        left: '1%',
        right: '1%',
        bottom: '18%',
        containLabel: true
      }
    ],
    /** 要渲染的charts系列配置 */
    series: getSeries(chartData, min, max, demand, isGroupeLink, selectType, meterModel, max1),
    /** 全局坐标轴指示器: https://echarts.apache.org/zh/option.html#axisPointer */
    axisPointer: {
      link: [ // 不同轴的 axisPointer 可以进行联动: https://echarts.apache.org/zh/option.html#axisPointer.link
        {
          xAxisIndex: 'all'
        }
      ],
      label: {
        backgroundColor: '#777'
      }
    },
    /** 提示框组件的配置 */
    tooltip: createTooltip({ isGroupeLink, selectType, meterModel }),
    /** 图例 */
    legend: getlegend(chartData, isGroupeLink, selectType, meterModel),
    /** 区域缩放组件：https://echarts.apache.org/zh/option.html#dataZoom */
    dataZoom: [
      {
        // type: 'slider', // 滑动条型数据区域缩放组件
        height: 12,
        bottom: 8,
        textStyle: {
          color: '#8392A5' // dataZoom 文字的颜色
        },
        // 两侧缩放手柄的 icon 形状，支持路径字符串
        handleIcon: 'path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
        handleSize: '60%',
        dataBackground: {
          areaStyle: {
            color: '#8392A5'
          },
          lineStyle: {
            opacity: 0.8,
            color: '#8392A5'
          }
        },
        brushSelect: true, // 开启刷选功能。在下图的 brush 区域可以按住鼠标左键后框选出选中部分。
        xAxisIndex: [0, 1]
      },
      {
        type: 'inside', // 内置型数据区域缩放组件。在图像上可使用鼠标缩放和拖动。
        xAxisIndex: [0, 1]
      }
    ],
    /** 禁止刷新图表时的动画效果 */
    animation: false,
  }
}


const getSeries = (chartData = {}, min, max, demand, isGroupeLink, selectType, meterModel = 1, y1Max) => {
  const soc = chartData.socChartData;
  const power = chartData.powerChartData;
  const strategyChartData = chartData.strategyChartData ?? [];
  const thirdPowerChartData = chartData.thirdPowerChartData ?? [];
  const historyTimePointsChartData = chartData.historyTimePointsChartData ?? [];

  // 当选择方阵为整站时，判断是否显示需量保护阈值、策略、虚拟电厂调度功率
  // 如果是实体表（meterModel == 1）才展示，如果是虚拟表（meterModel == 0）不展示
  // 选择其他（eLink/eBlock）时正常展示
  const isStation = selectType === 'station';
  const shouldShowForStation = isStation && meterModel === 1; // 整站且实体表才展示

  const series = [
    {
      name: $str.getKey('数据分析/SOC(%)'),
      type: 'line',
      xAxisIndex: 0,
      yAxisIndex: 0,
      showSymbol: false, // 是否显示数据位置的symbol符号：否，只在 tooltip hover 的时候显示。
      smooth: true,
      itemStyle: {
        color: '#8DD3F5'
      },
      areaStyle: { // 设置从X轴到line上数据处的区域的样式
        color: {
          type: 'linear', // 线性渐变
          x: 0, x2: 0, y: 0, y2: 1, // 颜色的渐变方向：x >>> x2;  y >>> y2;
          colorStops: [{
            offset: 0, color: 'rgba(141, 211, 245, 0.5)' // 0% 处的颜色
          }, {
            offset: 1, color: 'rgba(141, 211, 245, 0.01)' // 100% 处的颜色
          }],
          global: false // 缺省为 false
        }
      },
      data: soc, // 线数据
    },
    {
      name: $str.getKey('数据分析/功率(kW)'),
      type: 'line',
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
      smooth: true,
      itemStyle: {
        color: '#39D8BB'
      },
      data: power,
    },
  ];

  if (!isStation || shouldShowForStation) {
    // 若需量值超出 Y 轴上限，把 markLine 钳制到 Y 轴顶端（贴顶显示），标签仍显示真实需量
    const demandNum = Number(demand);
    const validDemand = !Number.isNaN(demandNum);
    const isOverflow = validDemand && y1Max != null && demandNum > y1Max;
    const markYAxis = isOverflow ? y1Max : demandNum;
    const demandThresholdName = $str.getKey('数据分析/需量保护阈值');
    series.push({
      name: demandThresholdName,
      type: 'line',
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
      color: 'red',
      markLine: { // 图表标线
        data: [
          {
            name: demandThresholdName, // 标线的名称，可以和系列名称不一致
            yAxis: markYAxis, // 若超出 Y 轴范围则钳到顶端，避免被裁剪不可见
          },
        ],
        symbol: ['none', 'none'],
        lineStyle: { //标注线样式
          normal: {
            type: 'solid',
            color: 'red', //标注线颜色
          },
          zIndex: 100,
        },
        label: {
          position: 'insideEndTop', // 文本显示位置
          formatter() {
            // 标签始终展示真实的需量值，即便线被钳到 Y 轴顶端
            return `${demandThresholdName}: ${validDemand ? demandNum : '--'}`;
          },
          color: 'red'
        },
      },
    });
  }

  if (chartData.isShowStrategyLine && (!isStation || shouldShowForStation)) {
    series.push({
      name: $str.getKey('数据分析/策略'),
      type: 'line',
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
      lineStyle: {
        type: 'dashed',
        width: 1
      },
      data: strategyChartData,
    })
  }

  if (chartData.isShowThirdPowerLine && (!isStation || shouldShowForStation)) {
    series.push({
      name: $str.getKey('数据分析/虚拟电厂调度功率'),
      data: thirdPowerChartData,
      type: 'line',
      xAxisIndex: 1,
      yAxisIndex: 1,
      symbol: 'circle',
      symbolSize: 0,
      color: '#DE00FF',
    })
  }

  if (chartData.isShowHistoryPoints) {
    const data = historyTimePointsChartData.map(it => {
      return {
        name: $str.getKey('数据分析/策略修改时间'),
        xAxis: it,
      }
    })

    const chartItem = {
      name: $str.getKey('数据分析/策略修改点'),
      type: 'line',
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
      color: '#6A00FB',
      markLine: { // 图表标线
        data,
        lineStyle: { //标注线样式
          normal: {
            color: '#6A00FB', //标注线颜色
            type: 'dashed',
            type: [10, 5],
          },
          zIndex: 100,
        },
        label: {
          show: false,
          position: 'insideEndTop', // 文本显示位置
          formatter(params) {
            return `${params.name}: ${params.value}`
          },
          color: '#6A00FB',
        },
      },
    };
    series.push(chartItem)
  }

  return series;
}

const getlegend = (chartData = {}, isGroupeLink, selectType, meterModel = 1) => {
  // 当选择方阵为整站时，判断是否显示需量保护阈值、策略、虚拟电厂调度功率
  // 如果是实体表（meterModel == 1）才展示，如果是虚拟表（meterModel == 0）不展示
  // 选择其他（eLink/eBlock）时正常展示
  const isStation = selectType === 'station';
  const shouldShowForStation = isStation && meterModel === 1; // 整站且实体表才展示

  const socName = $str.getKey('数据分析/SOC(%)');
  const powerName = $str.getKey('数据分析/功率(kW)');
  const demandThresholdName = $str.getKey('数据分析/需量保护阈值');
  const strategyName = $str.getKey('数据分析/策略');
  const thirdPowerName = $str.getKey('数据分析/虚拟电厂调度功率');
  const historyPointName = $str.getKey('数据分析/策略修改点');

  const selected = {
    [socName]: true,
    [powerName]: true,
  }

  // 添加需量保护阈值
  if (!isStation || shouldShowForStation) {
    selected[demandThresholdName] = true;
  }

  if (chartData.isShowStrategyLine && (!isStation || shouldShowForStation)) {
    selected[strategyName] = false;
  }
  if (chartData.isShowThirdPowerLine && (!isStation || shouldShowForStation)) {
    selected[thirdPowerName] = false;
  }
  if (chartData.isShowHistoryPoints) {
    selected[historyPointName] = false;
  }

  // 构建图例数据数组
  const legendData = [socName, powerName];

  // 添加需量保护阈值
  if (!isStation || shouldShowForStation) {
    legendData.push(demandThresholdName);
  }

  // 添加其他可选图例
  if (chartData.isShowStrategyLine && (!isStation || shouldShowForStation)) {
    legendData.push(strategyName);
  }
  if (chartData.isShowThirdPowerLine && (!isStation || shouldShowForStation)) {
    legendData.push(thirdPowerName);
  }
  if (chartData.isShowHistoryPoints) {
    legendData.push(historyPointName);
  }

  return {
    data: legendData,
    bottom: 40,
    icon: 'circle',
    itemGap: 20,
    textStyle: {
      color: '#999'
    },
    selected: selected
  }
}

function createTooltip(config = {}) {
  const { isGroupeLink, selectType, meterModel = 1 } = config;
  const isStation = selectType === 'station';
  const shouldShowForStation = isStation && meterModel === 1; // 整站且实体表才展示

  const timeFormatter = (value) => {
    // 格式化时间：如果是时间戳，转换为年月日时分秒；如果是时间字符串，直接使用
    let timeValue = value;
    if (typeof timeValue === 'number' || (typeof timeValue === 'string' && /^\d+$/.test(timeValue))) {
      // 判断是否是时间戳（毫秒或秒）
      const timestamp = Number(timeValue);
      const isMillisecond = timestamp > 946684800000; // 2000-01-01的时间戳（毫秒）
      timeValue = dayjs(isMillisecond ? timestamp : timestamp * 1000).format('YYYY-MM-DD HH:mm:ss');
    } else if (timeValue instanceof Date) {
      timeValue = dayjs(timeValue).format('YYYY-MM-DD HH:mm:ss');
    }
    return timeValue;
  };

  return {
    trigger: 'axis',
    axisPointer: {
      type: 'cross'
    },
    className: 'chart-tooltip',
    backgroundColor: 'rgba(255, 255, 255, 0.90)',
    borderWidth: 0,
    textStyle: {
      color: '#6A727C'
    },
    formatter: (params) => {
      let time = params[0].axisValue;
      time = timeFormatter(time);

      const str = (list) => {
        let t = '';
        const demandThresholdName = $str.getKey('数据分析/需量保护阈值');
        for (const item of list) {
          // 如果是整站且是虚拟表，过滤掉需量保护阈值、策略、虚拟电厂调度功率
          if (isStation && !shouldShowForStation) {
            const strategyName = $str.getKey('数据分析/策略');
            const thirdPowerName = $str.getKey('数据分析/虚拟电厂调度功率');
            if (item.seriesName === demandThresholdName ||
              item.seriesName === strategyName ||
              item.seriesName === thirdPowerName) {
              continue;
            }
          }

          const value = Array.isArray(item.data) ? item.data[1] : item.data;
          t += /* html */`
            <div class="chart-tooltip__series" style="display: flex; align-items: center; justify-content: space-between; gap: 8px; line-height: 1.5;">
              <div style="display: flex; align-items: center; gap: 6px; flex: 1;">
                <div class="chart-tooltip__point" style="background: ${item.color}; width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; display: inline-block; vertical-align: middle;"></div>
                <div class="chart-tooltip__series-name" style="display: inline-block; vertical-align: middle;">${item.seriesName}</div>
              </div>
              <div class="chart-tooltip__value" style="flex-shrink: 0; display: inline-block; vertical-align: middle; text-align: right;">${value ?? '--'}</div>
            </div>`;
        }
        return t;
      };

      return /* html */`
        <div class="chart-tooltip__time" style="margin-bottom: 12px;">${time}</div>
        <div class="chart-tooltip__items-container">${str(params)}</div>        
      `;
    }
  }
}

function getY0MinMaxNum() {
  return { min: 0, max: 100 }
}

/**
 * 获取坐标系最大值和最小值
 * >>> Y 轴只按实际曲线数据（策略/虚拟电厂调度功率/功率）自适应，
 * >>> 需量保护阈值不参与 min/max 计算，超出范围时其 markLine 以图外箭头呈现
 * @param {*} chartData
 * @returns
 */
function getY1MinMaxNum(chartData) {
  let min = 0;
  let max = 0;
  // 策略
  if (chartData.isShowStrategyLine) {
    chartData.strategyChartData.forEach(it => {
      if (Array.isArray(it) && it.length > 1) {
        const num = Number(it[1]);
        if (num > max) { max = num; }
        if (num < min) { min = num; }
      }
    });
  }

  // 虚拟电厂调度功率
  if (chartData.isShowThirdPowerLine) {
    chartData.thirdPowerChartData.forEach(it => {
      if (Array.isArray(it) && it.length > 1) {
        const num = Number(it[1]);
        if (num > max) { max = num; }
        if (num < min) { min = num; }
      }
    });
  }

  // 功率
  chartData.powerChartData.forEach(it => {
    if (Array.isArray(it) && it.length > 1) {
      const num = Number(it[1]);
      if (num > max) { max = num; }
      if (num < min) { min = num; }
    }
  });

  // 需量：不参与 Y 轴范围计算，避免大数值把 Y 轴顶起来导致其他曲线被压扁
  // 需量保护阈值仍以 markLine 展示，超出范围时 ECharts 会在坐标系边缘显示箭头

  max = Math.ceil(max / 10) * 10;
  min = Math.floor(min / 10) * 10;

  if (min == 0 && max == 0) {
    return { min: 0, max: 100 }
  }

  return { min, max }
}
